import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, PartyPopper, Tent, Sparkles, Fish } from "lucide-react";
import { api } from "../../lib/api";

/**
 * LaunchCountdownBar — slim global bar above the Header on every page.
 *
 * Three visual states, determined purely by current time vs LAUNCH_DATE:
 *
 *   1) PRE-LAUNCH (now → launch day)
 *      → Thin orange-on-black bar with the live countdown + recent-members
 *        marquee scrolling underneath it. Both visible together so we
 *        start building social proof immediately, not on launch day.
 *
 *   2) LAUNCH DAY (the 24h window of LAUNCH_DATE)
 *      → BIG bright "🎉 WE'VE LAUNCHED!" hero strip on day-of, gradient
 *        background, sparkles. Marquee still rolls below it.
 *
 *   3) POST-LAUNCH (after launch day's window has passed)
 *      → Just the marquee. Countdown + launch splash are gone forever.
 *
 * Marquee uses two interleaved copies of the same name list to keep the
 * scroll seamless when the first set rolls off the right edge.
 * Auto-hides per-day if dismissed (so it doesn't nag forever).
 */
const LAUNCH_DATE = new Date("2026-05-31T00:00:00Z");
const LAUNCH_DAY_MS = 24 * 3600 * 1000;
const STORAGE_KEY = "cc_launch_bar_closed_v3";

const HERO_EMOJI = ["🏕", "🎉", "🔥", "✨", "🌲", "👋", "⛺"];

function pad(n) { return String(n).padStart(2, "0"); }

function useNow(intervalMs = 1000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

export default function LaunchCountdownBar() {
  const now = useNow(1000);
  const t = now.getTime();
  const launched = t >= LAUNCH_DATE.getTime();
  const isLaunchDay = launched && (t - LAUNCH_DATE.getTime() < LAUNCH_DAY_MS);
  // iter117 — after the LIVE NOW launch-day window passes, run a 30-day
  // "Big changes coming" tease so visitors know we're not done shipping.
  const POST_LAUNCH_TEASE_MS = 30 * LAUNCH_DAY_MS;
  const isPostLaunchTease =
    launched &&
    !isLaunchDay &&
    (t - LAUNCH_DATE.getTime() < LAUNCH_DAY_MS + POST_LAUNCH_TEASE_MS);

  // Per-day dismiss so the bar re-appears next morning (kept nudgy without nagging)
  const todayKey = now.toISOString().slice(0, 10);
  const [closedFor, setClosedFor] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
  });
  const isClosed = closedFor === todayKey;
  const close = () => {
    try { localStorage.setItem(STORAGE_KEY, todayKey); } catch { /* ignore */ }
    setClosedFor(todayKey);
  };

  // iter121 — Only the banner portion (countdown / launch-day / tease) is
  // per-day dismissable. The MembersMarquee underneath stays visible all the
  // time because it's the social-proof "X just joined the community" ticker
  // that brings life to the top of every page. Previously the early `return
  // null` killed both — bug fix below.
  const showBanner = !isClosed;

  return (
    <div data-testid="launch-countdown-bar">
      {showBanner && !launched && <PreLaunchCountdown now={now} onClose={close} />}
      {showBanner && isLaunchDay && <LaunchDayHero onClose={close} />}
      {showBanner && isPostLaunchTease && <PostLaunchTease onClose={close} />}
      <MembersMarquee />
    </div>
  );
}


// ----------------------------------------------------------------------------
// Members marquee — always visible. Last ~8 signups scrolling like a train.
// ----------------------------------------------------------------------------
function MembersMarquee() {
  const [members, setMembers] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = () => {
      api.get("/community/recent-members?limit=8")
        .then(({ data }) => { if (alive) setMembers(data?.members || []); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 90_000); // refresh every 90s
    return () => { alive = false; clearInterval(t); };
  }, []);

  // While the list is empty, show a single placeholder strip so the bar
  // doesn't look empty/broken on first paint.
  const items = members.length > 0
    ? members
    : [{ first_name: "Sarah" }, { first_name: "Ben" }, { first_name: "Jenna" }, { first_name: "Tom" }, { first_name: "Marybeth" }];

  // Duplicate so when the first set scrolls off the left, the second
  // takes over without a visible gap.
  const looped = [...items, ...items];

  return (
    <div
      className="bg-[#fff4ee] border-b hairline overflow-hidden relative"
      data-testid="members-marquee"
      aria-label="Recent members joining CampCompass"
    >
      <style>{`
        @keyframes ccMarquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
      <div
        className="flex whitespace-nowrap py-1.5"
        style={{
          animation: "ccMarquee 35s linear infinite",
          willChange: "transform",
        }}
      >
        {looped.map((m, i) => (
          <span
            key={`${m.first_name}-${i}`}
            className="inline-flex items-center gap-1.5 mono text-[11px] sm:text-[12px] text-[#9a3520] px-4 sm:px-6"
            data-testid={`marquee-member-${i}`}
          >
            <span aria-hidden>{HERO_EMOJI[i % HERO_EMOJI.length]}</span>
            <strong className="text-[#1f1f1f]">{m.first_name}</strong>
            <span>just joined the camping community</span>
            <span className="text-[#cc7a4b]" aria-hidden>·</span>
          </span>
        ))}
      </div>
    </div>
  );
}


// ----------------------------------------------------------------------------
// Pre-launch countdown — slim strip. Compact so the marquee below has room.
// ----------------------------------------------------------------------------
function PreLaunchCountdown({ now, onClose }) {
  const remaining = useMemo(() => {
    const ms = Math.max(0, LAUNCH_DATE.getTime() - now.getTime());
    const days = Math.floor(ms / 86400000);
    const hours = Math.floor((ms % 86400000) / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return { days, hours, minutes, seconds };
  }, [now]);

  return (
    <div className="bg-[#111] text-white relative overflow-hidden" data-testid="pre-launch-countdown">
      <div className="relative max-w-7xl mx-auto px-3 sm:px-5 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-3 text-[12px] sm:text-[13px] min-w-0">
          <Calendar className="w-4 h-4 flex-shrink-0 text-[#ff4500]" strokeWidth={2.2} />
          <span className="font-semibold whitespace-nowrap hidden sm:inline">Going live in</span>
          <span className="font-semibold whitespace-nowrap sm:hidden">Launch in</span>
          <CountTile n={remaining.days} label="d" />
          <CountTile n={remaining.hours} label="h" />
          <CountTile n={remaining.minutes} label="m" />
          <CountTile n={remaining.seconds} label="s" pulse />
          <span className="text-white/80 hidden md:inline ml-1">
            · install + sign up now → no queue at launch.
          </span>
        </div>
        <button
          onClick={onClose}
          aria-label="Dismiss"
          className="text-white/60 hover:text-white text-xs leading-none px-1.5 py-1 transition-colors flex-shrink-0"
          data-testid="dismiss-launch-bar"
        >✕</button>
      </div>
    </div>
  );
}

function CountTile({ n, label, pulse = false }) {
  return (
    <span className={`inline-flex items-baseline gap-0.5 mono tracking-tight bg-[#222] border border-[#333] px-1.5 py-0.5 text-[12px] sm:text-[13px] ${pulse ? "text-[#ff4500]" : "text-white"}`}>
      <strong className="font-bold">{pad(n)}</strong>
      <span className="text-[9px] sm:text-[10px] text-white/60 uppercase">{label}</span>
    </span>
  );
}


// ----------------------------------------------------------------------------
// Launch day hero — BIG BRIGHT BEAUTIFUL "WE'VE LAUNCHED" strip.
// Shows on launch day only; auto-disappears after 24h.
// Includes a "Share with mates" button (native share sheet on mobile,
// copy-to-clipboard fallback on desktop).
// ----------------------------------------------------------------------------
function LaunchDayHero({ onClose }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = "https://camp-compass.uk";
  const shareText = "🏕 LIVE NOW!! CampCompass UK just launched — 22,000+ UK campsites in one app. Sign up for a 14-day FREE Pro trial. No queue, no catch:";

  const onShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: "CampCompass UK", text: shareText, url: shareUrl });
        return;
      }
    } catch { /* user cancelled — fall through to copy */ }
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard blocked — silent */ }
  };

  return (
    <div
      className="relative overflow-hidden text-white shadow-[0_2px_0_0_#111]"
      style={{
        background:
          "linear-gradient(90deg, #ff4500 0%, #ff7a00 35%, #ffb000 60%, #ff7a00 80%, #ff4500 100%)",
        backgroundSize: "200% 100%",
        animation: "ccLaunchSweep 6s linear infinite",
      }}
      data-testid="launch-day-hero"
    >
      <style>{`
        @keyframes ccLaunchSweep {
          0%   { background-position: 0% 0%; }
          100% { background-position: 200% 0%; }
        }
        @keyframes ccLaunchPop {
          0%,100% { transform: scale(1); }
          50%     { transform: scale(1.08); }
        }
      `}</style>
      <div className="relative max-w-7xl mx-auto px-3 sm:px-5 py-2.5 sm:py-3.5 flex items-center gap-2 sm:gap-3">
        <PartyPopper
          className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0"
          strokeWidth={2.2}
          style={{ animation: "ccLaunchPop 1.6s ease-in-out infinite" }}
        />
        {/* Headline + sub all live in one flex column so text reflows
            cleanly. Sub line hidden on the tightest mobile widths. */}
        <div className="font-display tracking-tight leading-tight min-w-0 flex-1">
          <div className="text-sm sm:text-base md:text-lg">
            <span className="font-bold uppercase tracking-wider">LIVE NOW!!</span>
            <span className="hidden md:inline font-normal ml-2">
              Sign up for a 14-day FREE Pro trial · 22,000+ UK campsites 🏕
            </span>
            <span className="hidden sm:inline md:hidden font-normal ml-2">
              14-day free Pro trial 🏕
            </span>
          </div>
          <div className="text-[11px] sm:text-[12px] font-normal opacity-95 mt-0.5 truncate">
            Tag every camper you know 👇
          </div>
        </div>
        <button
          onClick={onShare}
          className="inline-flex items-center gap-1.5 bg-white text-[#ff4500] font-bold text-[11px] sm:text-[12px] uppercase tracking-wider px-2.5 sm:px-3.5 py-1.5 sm:py-2 border-2 border-white hover:bg-[#fff4ee] transition-colors flex-shrink-0 whitespace-nowrap"
          data-testid="launch-hero-share"
        >
          <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5" strokeWidth={2.5} />
          {copied ? "Link copied!" : "Share"}
        </button>
        <button
          onClick={onClose}
          aria-label="Dismiss"
          className="text-white/80 hover:text-white text-lg leading-none px-1 py-1 transition-colors flex-shrink-0"
          data-testid="dismiss-launch-hero"
        >✕</button>
      </div>
    </div>
  );
}


// ----------------------------------------------------------------------------
// PostLaunchTease — replaces the LIVE NOW hero once the launch-day window
// closes. Runs for 30 days after launch (iter117). iter127: now teases the
// new one-tap search-and-compare flow that ships with the home widget +
// simplified search reel. Condensed to a single line on mobile.
// ----------------------------------------------------------------------------
function PostLaunchTease({ onClose }) {
  return (
    <div
      className="w-full text-white relative overflow-hidden"
      style={{ background: "linear-gradient(90deg,#ff4500 0%,#ff6a00 50%,#ffaa00 100%)" }}
      data-testid="post-launch-tease"
    >
      {/* Pulsing glow accent on the left edge to draw the eye */}
      <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#fde047] animate-pulse" />
      <Link
        to="/#search"
        className="block hover:brightness-110 transition-[filter]"
        data-testid="post-launch-tease-link"
      >
        <div className="max-w-6xl mx-auto pl-4 pr-3 sm:pl-5 sm:pr-4 py-2 sm:py-2.5 flex items-center gap-2 sm:gap-3">
          <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/95 text-[#ff4500] flex items-center justify-center shadow-md">
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.6} />
          </div>
          <div className="font-display tracking-tight leading-tight min-w-0 flex-1 text-[13.5px] sm:text-[15px] md:text-base">
            <span className="font-extrabold uppercase tracking-wider text-white drop-shadow-[0_1px_0_rgba(0,0,0,0.25)]">
              NEW · 1-tap search &amp; compare
            </span>
            <span className="hidden sm:inline font-medium ml-2 text-white/95">
              Tick what matters · compare campsites near you 🏕️
            </span>
            <span className="sm:hidden font-medium ml-1.5 text-white/95">
              · Try it 🏕️
            </span>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 bg-[#0e0e0e] text-white text-[10.5px] font-extrabold uppercase tracking-wider rounded-full shadow-md">
            Try it now →
          </span>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
            aria-label="Dismiss"
            className="text-white/90 hover:text-white text-lg leading-none px-1 py-0.5 transition-colors flex-shrink-0"
            data-testid="dismiss-post-launch-tease"
          >✕</button>
        </div>
      </Link>
    </div>
  );
}

